// Builds the Sentry span name for a sampled API request: METHOD + a path with
// every id-shaped segment collapsed to `:id`, so we never leak a concrete
// UUID/portal-id/numeric-id into a span name (which is high cardinality and
// would otherwise mint a new-looking "route" per request).
const UUID_SEGMENT =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ALL_DIGIT_SEGMENT = /^\d+$/;
// Portal/resource ids used across the API are base62 (alphanumeric),
// 9-12 characters. A plain route segment (verb, resource name) is real
// English and, at this length, is consistently either all-lowercase or all
// one case with no digits — so either a digit or a mix of upper/lower case
// is a safe signal this is a random id rather than a word: a digit rules out
// "companies"/"workspace", and mixed case rules out an all-letter id like a
// product-store shortId ("aBcDeFgHi") that would otherwise sneak through.
const BASE62_LENGTH_SEGMENT = /^[0-9a-zA-Z]{9,12}$/;
const HAS_DIGIT = /[0-9]/;
const HAS_LOWERCASE = /[a-z]/;
const HAS_UPPERCASE = /[A-Z]/;

const isBase62IdSegment = (segment: string): boolean =>
  BASE62_LENGTH_SEGMENT.test(segment) &&
  (HAS_DIGIT.test(segment) ||
    (HAS_LOWERCASE.test(segment) && HAS_UPPERCASE.test(segment)));

// App builder ids are exactly `app-` plus eight hex chars from four random
// bytes. Hex can be all letters (`app-abcdefab`), so the digit signal used
// for other prefixed ids misses them.
const APP_BUILDER_ID_SEGMENT = /^app-[0-9a-f]{8}$/;

// Other ids carry a short type prefix. The hyphen keeps them out of the
// base62 rule above, so match the shape directly: a short lowercase prefix,
// a hyphen, and a digit-bearing tail. The digit keeps hyphenated route
// words (`app-builders`, `onboarding-task-status`) from collapsing.
const PREFIXED_ID_SEGMENT = /^[a-z]{1,8}-[0-9a-zA-Z]{6,}$/;

const isPrefixedIdSegment = (segment: string): boolean => {
  if (APP_BUILDER_ID_SEGMENT.test(segment)) {
    return true;
  }
  if (!PREFIXED_ID_SEGMENT.test(segment)) {
    return false;
  }
  const tail = segment.slice(segment.indexOf('-') + 1);
  return HAS_DIGIT.test(tail);
};

const isIdLikeSegment = (segment: string): boolean =>
  UUID_SEGMENT.test(segment) ||
  ALL_DIGIT_SEGMENT.test(segment) ||
  isBase62IdSegment(segment) ||
  isPrefixedIdSegment(segment);

export const normalizeApiPathForSpanName = (path: string): string =>
  path
    .split('/')
    .map((segment) => (segment && isIdLikeSegment(segment) ? ':id' : segment))
    .join('/');

export const buildApiSpanName = (method: string, path: string): string =>
  `${method.toUpperCase()} ${normalizeApiPathForSpanName(path)}`;
